import { ChangeEvent, ReactNode } from "react";
import { ViewItem, viewItemProps } from "../../../../Components/Views/ViewItem";
import { MailHtmlFrame, formatFileSize, getInitial, linkifyText, sanitizeEmailHtml } from "../../../../Components/Views/MailHtmlFrame";
import { controllers } from "../../../../Controllers/Controllers";
import { ComposeEmailFields, EmailMessageType, EmailsPagination } from "../../../../DataTypes/mail";
import { getRoundedLocalDateTimeString } from "../../../../Helpers/Helpers";
import '../outreach-styles.css'
import '../Categories/categories-styles.css'
import './mail-styles.css'

const PER_PAGE = 25
const SEARCH_DEBOUNCE_MS = 350

const emptyReplyFields: ComposeEmailFields = {
    to: '',
    cc: '',
    subject: '',
    body_text: '',
    include_signature: true,
}

const getCounterpart = (email: EmailMessageType) =>
    email.direction === 'outgoing' ? email.to_addresses : email.from_address

type MailPageProps = viewItemProps & {
    folder?: 'inbox' | 'sent';
}

type MailPageState = {
    emails: EmailMessageType[];
    pagination: EmailsPagination | null;
    loading: boolean;
    error: string | null;
    needsAuth: boolean;
    folder: 'inbox' | 'sent';
    search: string;
    page: number;

    selectedEmail: EmailMessageType | null;
    threadEmails: EmailMessageType[];
    threadLoading: boolean;
    threadError: string | null;
    updatingEmailId: number | null;

    syncing: boolean;
    syncMessage: string | null;

    replyOpen: boolean;
    replyFields: ComposeEmailFields;
    replySending: boolean;
    replyError: string | null;
}

class MailPage extends ViewItem {
    props: MailPageProps;
    state: MailPageState;
    searchTimer: any = null;
    syncPollTimer: any = null;
    unmounted: boolean = false;

    constructor(props: MailPageProps) {
        super(props)
        this.props = props
        this.state = {
            emails: [],
            pagination: null,
            loading: true,
            error: null,
            needsAuth: false,
            folder: props.folder || 'inbox',
            search: '',
            page: 1,

            selectedEmail: null,
            threadEmails: [],
            threadLoading: false,
            threadError: null,
            updatingEmailId: null,

            syncing: false,
            syncMessage: null,

            replyOpen: false,
            replyFields: { ...emptyReplyFields },
            replySending: false,
            replyError: null,
        }
    }

    componentDidMount(): void {
        this.loadEmails()
    }

    componentDidUpdate(prevProps: MailPageProps): void {
        // React Router reuses this same instance across /outreach/inbox and
        // /outreach/sent (same component type, same slot in the tree) — it
        // does NOT remount, so the constructor's props.folder read only
        // happens once. A prop change here has to be synced into state
        // (what actually drives the render) and re-fetched by hand.
        if (this.props.folder && this.props.folder !== prevProps.folder) {
            this.setState({
                folder: this.props.folder,
                page: 1,
                selectedEmail: null,
                threadEmails: [],
                replyOpen: false,
            }, this.loadEmails)
        }
    }

    componentWillUnmount(): void {
        this.unmounted = true
        if (this.searchTimer) clearTimeout(this.searchTimer)
        if (this.syncPollTimer) clearTimeout(this.syncPollTimer)
    }

    loadEmails = async () => {
        const { folder, search, page } = this.state
        this.setState({ loading: true, error: null, needsAuth: false })
        try {
            if (!controllers().auth.userToken) {
                this.setState({ loading: false, needsAuth: true })
                return
            }
            const response = await controllers().mail.listEmails({
                folder,
                search: search || undefined,
                page,
                per_page: PER_PAGE,
            })
            if (!response || response.statusCode !== 200) {
                this.setState({ loading: false, error: response?.error || 'Failed to load emails.' })
                return
            }
            this.setState({
                emails: response.data?.emails || [],
                pagination: response.data?.pagination || null,
                loading: false,
            })
        } catch (e) {
            this.setState({ loading: false, error: 'Failed to load emails.' })
        }
    }

    handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        this.setState({ search: value })
        if (this.searchTimer) clearTimeout(this.searchTimer)
        this.searchTimer = setTimeout(() => {
            this.setState({ page: 1 }, this.loadEmails)
        }, SEARCH_DEBOUNCE_MS)
    }

    goToPage = (page: number) => {
        const { pagination } = this.state
        if (pagination && (page < 1 || page > pagination.total_pages)) return
        this.setState({ page }, this.loadEmails)
    }

    // Sync runs in a background thread server-side and returns immediately
    // (mailbox fetches can take a couple minutes), so there's nothing to
    // await here — instead poll mail/settings until last_synced_at moves
    // forward or last_sync_error gets set.
    handleSync = async () => {
        this.setState({ syncing: true, syncMessage: null })

        const before = await controllers().mail.getSettings()
        const baselineSyncedAt = before?.data?.last_synced_at || null

        const triggerResponse = await controllers().mail.syncInbox()
        if (!triggerResponse || triggerResponse.statusCode !== 200) {
            this.setState({ syncing: false, syncMessage: triggerResponse?.error || 'Sync failed to start.' })
            return
        }

        this.setState({ syncMessage: 'Sync started — checking for updates...' })
        this.pollSyncStatus(baselineSyncedAt, 0)
    }

    pollSyncStatus = (baselineSyncedAt: string | null, attempt: number) => {
        const MAX_ATTEMPTS = 8
        const POLL_INTERVAL_MS = 3000

        this.syncPollTimer = setTimeout(async () => {
            const response = await controllers().mail.getSettings()
            if (this.unmounted) return
            const account = response?.data

            if (account?.last_sync_error) {
                this.setState({ syncing: false, syncMessage: `Sync failed: ${account.last_sync_error}` })
                return
            }

            if (account?.last_synced_at && account.last_synced_at !== baselineSyncedAt) {
                this.setState({ syncing: false, syncMessage: 'Inbox synced.' })
                this.loadEmails()
                return
            }

            if (attempt + 1 >= MAX_ATTEMPTS) {
                this.setState({ syncing: false, syncMessage: 'Still syncing in the background — check back in a moment.' })
                this.loadEmails()
                return
            }

            this.pollSyncStatus(baselineSyncedAt, attempt + 1)
        }, POLL_INTERVAL_MS)
    }

    updateEmailLocally = (emailId: number, fields: Partial<EmailMessageType>) => {
        this.setState((prev: MailPageState) => ({
            emails: prev.emails.map(email => email.id === emailId ? { ...email, ...fields } : email),
            threadEmails: prev.threadEmails.map(email => email.id === emailId ? { ...email, ...fields } : email),
        }))
    }

    openEmail = (email: EmailMessageType) => {
        this.setState({
            selectedEmail: email,
            replyOpen: false,
            replyError: null,
        })
        this.loadThread(email)
    }

    // get_email_detail_with_history also marks the requested message read
    // server-side (same as get_email_detail used to) — no separate call needed.
    loadThread = async (email: EmailMessageType) => {
        this.setState({ threadLoading: true, threadError: null })
        const response = await controllers().mail.getEmailThread(email.id)
        if (this.state.selectedEmail?.id !== email.id) return // user already moved on

        if (!response || response.statusCode !== 200) {
            this.setState({ threadLoading: false, threadError: response?.error || 'Failed to load conversation.', threadEmails: [email] })
            return
        }

        const messages: EmailMessageType[] = response.data?.emails || [email]
        const updatedTarget = messages.find(m => m.id === email.id) || email
        this.setState((prev: MailPageState) => ({
            threadEmails: messages.length ? messages : [email],
            threadLoading: false,
            emails: prev.emails.map(e => e.id === updatedTarget.id ? updatedTarget : e),
            selectedEmail: prev.selectedEmail?.id === updatedTarget.id ? updatedTarget : prev.selectedEmail,
        }))
    }

    handleMarkUnread = async (email: EmailMessageType) => {
        this.setState({ updatingEmailId: email.id })
        const response = await controllers().mail.markUnread(email.id)
        if (response && response.statusCode === 200) {
            this.updateEmailLocally(email.id, { is_read: false })
            this.setState((prev: MailPageState) => (
                prev.selectedEmail?.id === email.id ? { selectedEmail: { ...prev.selectedEmail, is_read: false } } : null
            ))
        }
        this.setState({ updatingEmailId: null })
    }

    handleDelete = async (email: EmailMessageType) => {
        const confirmed = window.confirm(`Delete this email locally? "${email.subject || '(no subject)'}"`)
        if (!confirmed) return
        this.setState({ updatingEmailId: email.id })
        const response = await controllers().mail.deleteEmail(email.id)
        if (response && response.statusCode === 200) {
            this.setState((prev: MailPageState) => ({
                emails: prev.emails.filter(e => e.id !== email.id),
                threadEmails: prev.threadEmails.filter(e => e.id !== email.id),
                selectedEmail: prev.selectedEmail?.id === email.id ? null : prev.selectedEmail,
            }))
        }
        this.setState({ updatingEmailId: null })
    }

    openReply = () => {
        const { selectedEmail } = this.state
        if (!selectedEmail) return
        const subject = selectedEmail.subject?.toLowerCase().startsWith('re:')
            ? selectedEmail.subject
            : `Re: ${selectedEmail.subject || ''}`
        this.setState({
            replyOpen: true,
            replyError: null,
            replyFields: {
                to: getCounterpart(selectedEmail),
                cc: '',
                subject,
                body_text: '',
                include_signature: true,
                lead_id: selectedEmail.lead_id || undefined,
                in_reply_to_message_id: selectedEmail.message_id,
            },
        })
    }

    closeReply = () => {
        if (this.state.replySending) return
        this.setState({ replyOpen: false })
    }

    setReplyField = (field: keyof ComposeEmailFields, value: string | boolean) => {
        this.setState((prev: MailPageState) => ({
            replyFields: { ...prev.replyFields, [field]: value }
        }))
    }

    handleSendReply = async () => {
        const { replyFields, selectedEmail } = this.state
        if (!replyFields.to.trim()) {
            this.setState({ replyError: 'At least one recipient is required.' })
            return
        }
        this.setState({ replySending: true, replyError: null })
        const response = await controllers().mail.sendEmail(replyFields)
        if (!response || response.statusCode !== 200) {
            this.setState({ replySending: false, replyError: response?.error || 'Failed to send reply.' })
            return
        }
        this.setState({ replySending: false, replyOpen: false })
        if (selectedEmail) this.loadThread(selectedEmail)
        if (this.state.folder === 'sent') this.loadEmails()
    }

    renderPagination = () => {
        const { pagination, page } = this.state
        if (!pagination || pagination.total_pages <= 1) return null
        return (
            <div className="outreach-pagination">
                <span className="outreach-pagination-info">
                    Page {pagination.current_page} of {pagination.total_pages} · {pagination.total_count} total
                </span>
                <div className="outreach-pagination-controls">
                    <button className="outreach-pagination-btn" disabled={!pagination.has_previous} onClick={() => this.goToPage(page - 1)}>
                        Previous
                    </button>
                    <button className="outreach-pagination-btn" disabled={!pagination.has_next} onClick={() => this.goToPage(page + 1)}>
                        Next
                    </button>
                </div>
            </div>
        )
    }

    renderList = () => {
        const { emails, folder, selectedEmail, updatingEmailId } = this.state
        return (
            <div className="mail-list">
                {emails.map(email => (
                    <div
                        key={email.id}
                        className={`mail-list-item ${!email.is_read ? 'mail-list-item-unread' : ''} ${selectedEmail?.id === email.id ? 'mail-list-item-active' : ''}`}
                        onClick={() => this.openEmail(email)}
                    >
                        <div className="mail-list-item-top">
                            <span className="mail-list-item-sender">
                                {!email.is_read && <span className="mail-unread-dot" />}
                                {folder === 'sent' ? email.to_addresses : email.from_address}
                            </span>
                            <span className="mail-list-item-date">
                                {getRoundedLocalDateTimeString(email.sent_at || email.created_at)}
                            </span>
                        </div>
                        <div className="mail-list-item-subject">{email.subject || '(no subject)'}</div>
                        {email.body_text && (
                            <div className="mail-list-item-snippet">{email.body_text}</div>
                        )}
                        <button
                            className="mail-list-item-delete"
                            onClick={(e) => { e.stopPropagation(); this.handleDelete(email) }}
                            disabled={updatingEmailId === email.id}
                            aria-label="Delete"
                            title="Delete"
                        >
                            ×
                        </button>
                    </div>
                ))}
            </div>
        )
    }

    renderReplyBox = () => {
        const { replyOpen, replyFields, replySending, replyError } = this.state
        if (!replyOpen) return null
        return (
            <div className="mail-reply-box">
                <div className="mail-reply-box-header">
                    <span className="mail-reply-box-title">Reply</span>
                    <button className="mail-thread-icon-btn" onClick={this.closeReply} disabled={replySending} aria-label="Cancel reply">
                        ×
                    </button>
                </div>

                {replyError && <div className="category-form-error">{replyError}</div>}

                <div className="category-form-group">
                    <label className="category-form-label">To</label>
                    <input
                        className="category-form-input"
                        value={replyFields.to}
                        onChange={(e) => this.setReplyField('to', e.target.value)}
                        disabled={replySending}
                    />
                </div>
                <div className="category-form-group">
                    <label className="category-form-label">Subject</label>
                    <input
                        className="category-form-input"
                        value={replyFields.subject}
                        onChange={(e) => this.setReplyField('subject', e.target.value)}
                        disabled={replySending}
                    />
                </div>
                <div className="category-form-group">
                    <textarea
                        className="category-form-textarea mail-reply-textarea"
                        value={replyFields.body_text}
                        onChange={(e) => this.setReplyField('body_text', e.target.value)}
                        placeholder="Write your reply..."
                        rows={6}
                        disabled={replySending}
                        autoFocus
                    />
                </div>
                <div className="mail-reply-box-actions">
                    <label className="category-form-checkbox-row mail-reply-signature-toggle">
                        <input
                            type="checkbox"
                            checked={replyFields.include_signature}
                            onChange={(e) => this.setReplyField('include_signature', e.target.checked)}
                            disabled={replySending}
                        />
                        Include signature
                    </label>
                    <div className="mail-reply-box-buttons">
                        <button className="outreach-modal-cancel" onClick={this.closeReply} disabled={replySending}>
                            Cancel
                        </button>
                        <button className="outreach-add-btn" onClick={this.handleSendReply} disabled={replySending}>
                            {replySending ? 'Sending...' : 'Send Reply'}
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    renderThreadPanel = () => {
        const { selectedEmail, threadEmails, threadLoading, threadError, updatingEmailId } = this.state
        const userToken = controllers().auth.userToken

        if (!selectedEmail) {
            return (
                <div className="mail-thread-panel mail-thread-empty">
                    <span className="outreach-muted">Select an email to view the conversation.</span>
                </div>
            )
        }

        return (
            <div className="mail-thread-panel">
                <div className="mail-thread-header">
                    <div className="mail-thread-header-info">
                        <h2 className="mail-thread-subject">{selectedEmail.subject || '(no subject)'}</h2>
                        <p className="mail-thread-participants">with {getCounterpart(selectedEmail)}</p>
                    </div>
                    <div className="mail-thread-header-actions">
                        <button className="outreach-edit-btn" onClick={this.openReply}>
                            Reply
                        </button>
                        <button
                            className="outreach-edit-btn"
                            onClick={() => this.handleMarkUnread(selectedEmail)}
                            disabled={updatingEmailId === selectedEmail.id}
                        >
                            Mark unread
                        </button>
                        <button
                            className="outreach-edit-btn category-delete-btn"
                            onClick={() => this.handleDelete(selectedEmail)}
                            disabled={updatingEmailId === selectedEmail.id}
                        >
                            Delete
                        </button>
                    </div>
                </div>

                {this.renderReplyBox()}

                {threadLoading && (
                    <div className="outreach-state">
                        <div className="outreach-spinner" />
                    </div>
                )}

                {!threadLoading && threadError && (
                    <div className="outreach-banner outreach-banner-error">
                        <span className="outreach-banner-text">{threadError}</span>
                    </div>
                )}

                {!threadLoading && !threadError && (
                    <div className="mail-thread-messages">
                        {threadEmails.map(msg => {
                            const isOutgoing = msg.direction === 'outgoing'
                            return (
                                <div key={msg.id} className={`mail-thread-message ${msg.id === selectedEmail.id ? 'mail-thread-message-focused' : ''}`}>
                                    <div className={`mail-thread-message-avatar ${isOutgoing ? 'mail-thread-message-avatar-you' : ''}`}>
                                        {isOutgoing ? 'Y' : getInitial(msg.from_address)}
                                    </div>
                                    <div className="mail-thread-message-content">
                                        <div className="mail-thread-message-header">
                                            <div className="mail-thread-message-from-block">
                                                <span className="mail-thread-message-from">
                                                    {isOutgoing ? 'You' : msg.from_address}
                                                </span>
                                                <span className="mail-thread-message-to">to {msg.to_addresses}</span>
                                            </div>
                                            <span className="mail-thread-message-date">
                                                {getRoundedLocalDateTimeString(msg.sent_at || msg.created_at)}
                                            </span>
                                        </div>
                                        {msg.body_html ? (
                                            <MailHtmlFrame html={sanitizeEmailHtml(msg.body_html, msg.attachments || [], userToken)} />
                                        ) : (
                                            <div className="mail-thread-message-body">
                                                {msg.body_text ? linkifyText(msg.body_text) : <span className="outreach-muted">(no content)</span>}
                                            </div>
                                        )}

                                        {msg.attachments?.some(a => !a.is_inline) && (
                                            <div className="mail-attachments">
                                                {msg.attachments.filter(a => !a.is_inline).map(att => (
                                                    <a
                                                        key={att.id}
                                                        className="mail-attachment-chip"
                                                        href={`${att.url}${userToken ? `?token=${userToken}` : ''}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        <span className="mail-attachment-name">{att.filename}</span>
                                                        <span className="mail-attachment-size">{formatFileSize(att.size)}</span>
                                                    </a>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                )}
            </div>
        )
    }

    render(): ReactNode {
        const { emails, loading, error, needsAuth, search, syncing, syncMessage } = this.state

        return (
            <div className="outreach-page">
                <div className="outreach-header">
                    <div className="outreach-title-header">
                        <h1 className="outreach-title">{this.props.folder == "sent" ? "Sent" : "Inbox"}</h1>
                        <p className="outreach-subtitle">Mail functionality</p>
                    </div>
                    <div className="outreach-toolbar">
                        <input
                            type="text"
                            className="outreach-search"
                            placeholder="Search subject, sender, or body..."
                            value={search}
                            onChange={this.handleSearchChange}
                        />
                        <button className="outreach-edit-btn" onClick={this.handleSync} disabled={syncing}>
                            {syncing ? 'Syncing...' : 'Sync now'}
                        </button>
                    </div>
                </div>

                {syncMessage && !loading && (
                    <div className="outreach-banner outreach-banner-info">
                        <span className="outreach-banner-text">{syncMessage}</span>
                        <button className="outreach-banner-action" onClick={() => this.setState({ syncMessage: null })}>
                            Dismiss
                        </button>
                    </div>
                )}

                {!loading && needsAuth && (
                    <div className="outreach-banner outreach-banner-info">
                        <span className="outreach-banner-text">Sign in to view your inbox.</span>
                        <button className="outreach-banner-action" onClick={() => controllers().modals.authModal.show()}>
                            Sign In
                        </button>
                    </div>
                )}

                {!loading && error && (
                    <div className="outreach-banner outreach-banner-error">
                        <span className="outreach-banner-text">{error}</span>
                        <button className="outreach-banner-action" onClick={this.loadEmails}>
                            Retry
                        </button>
                    </div>
                )}

                {loading && (
                    <div className="outreach-state">
                        <div className="outreach-spinner" />
                    </div>
                )}

                {!loading && !error && !needsAuth && emails.length === 0 && (
                    <div className="outreach-state outreach-empty">
                        No emails in {this.state.folder === 'sent' ? 'Sent' : 'Inbox'}.
                    </div>
                )}

                {!loading && !error && !needsAuth && emails.length > 0 && (
                    <div className="mail-layout">
                        <div className="mail-list-column">
                            {this.renderList()}
                            {this.renderPagination()}
                        </div>
                        {this.renderThreadPanel()}
                    </div>
                )}
            </div>
        )
    }
}

export { MailPage }
